import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { STOCK_POLICY_MENU_TITLE } from "@/modules/inventory/presentation/ui/stock-policy/stockPolicyLaoLabels";
import { TRANSFER_SECTION_STACK } from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { LowStockReportParams } from "@/modules/reports/presentation/api/client";
import { reportsApi } from "@/modules/reports/presentation/api/client";
import { useLowStockReportQuery } from "@/modules/reports/presentation/api/queries";
import {
  LowStockReportsFilter,
  lowStockReportParams,
  type LowStockReportsSearch,
} from "@/modules/reports/presentation/ui/low-stock/LowStockReportsFilter";
import { LowStockReportsTable } from "@/modules/reports/presentation/ui/low-stock/LowStockReportsTable";
import { downloadLowStockReportCsv } from "@/modules/reports/presentation/ui/low-stock/export-low-stock-report-csv";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { AlertTriangleIcon, FileDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export function LowStockReportsPage() {
  const search = useSearch({
    from: "/app/reports/inventory/low-stock",
  }) as LowStockReportsSearch;
  const [exporting, setExporting] = useState(false);

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });
  const branches: BranchRow[] = branchesData?.data ?? [];
  const hub = branches.find((b) => b.isDistributionHub);

  const reportParams = useMemo(
    () => lowStockReportParams(search, hub?.id),
    [search, hub?.id],
  );

  const query = useLowStockReportQuery(reportParams ?? undefined);

  const branchCode =
    branches.find((b) => b.id === reportParams?.branchId)?.code ?? "";

  const handleExport = async () => {
    if (!reportParams) {
      toast.error("ເລືອກສາຂາກ່ອນ");
      return;
    }
    setExporting(true);
    try {
      const exportParams: Omit<LowStockReportParams, "limit" | "offset"> = {
        branchId: reportParams.branchId,
        itemType: reportParams.itemType,
        categoryId: reportParams.categoryId,
      };
      const data = await reportsApi.getLowStockReportExport(exportParams);
      if (data.items.length === 0) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }
      downloadLowStockReportCsv(data.items, { branchCode });
      toast.success(
        `ສົ່ງອອກ CSV: ${data.items.length.toLocaleString("lo-LA")} ລາຍການ`,
      );
    } catch {
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ລາຍງານສາງ", to: "/app/reports/inventory" },
            { label: "ຕ່ຳກວ່າເກັນໄຂສຕັອກ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <AlertTriangleIcon className="size-5 text-amber-600" />
              ລາຍງານຕ່ຳກວ່າເງື່ອນໄຂ
            </h2>
            <p className="text-muted-foreground">
              ລາຍການທີ່ຄົງເຫຼືອ &lt; min ຕາມ {STOCK_POLICY_MENU_TITLE} · CSV
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exporting || !reportParams}
            onClick={() => void handleExport()}
            className="gap-2"
          >
            <FileDownIcon className="size-4" />
            {exporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ CSV"}
          </Button>
        </div>

        <div className={TRANSFER_SECTION_STACK}>
          <LowStockReportsFilter />

          {reportParams && query.data ? (
            <p className="text-muted-foreground text-sm">
              ພົບ{" "}
              <strong className="text-foreground tabular-nums">
                {query.data.total.toLocaleString("lo-LA")}
              </strong>{" "}
              ລາຍການ
              {branchCode ? ` · ${branchCode}` : ""}
            </p>
          ) : null}

          <LowStockReportsTable
            items={query.data?.items ?? []}
            isLoading={query.isLoading && !!reportParams}
          />
        </div>
      </Main>
    </>
  );
}
